/**
 * DONNÉES DE DÉMONSTRATION — Maison Duval
 * ========================================
 *
 * Ce fichier est **le seul endroit** où vivent des données de démonstration.
 * Il est produit par `scripts/generer-donnees-vitrine.py` à partir de
 * `mock-data/*.json` — ne pas le modifier à la main : régénérer.
 *
 * Les valeurs marquées « [RÉGLAGE] » sont publiées par l'API (`/v1/settings/public`)
 * en production. Aucun écran ne les recopie : les composants lisent `REGLAGES`.
 *
 * Passage à l'API réelle : `src/data/repository.ts` est la seule couche à changer.
 * Les types ci-dessous sont volontairement ceux de l'écran (pas ceux de la base) ;
 * `repository.ts` fait la correspondance.
 */

export type Modele = 'AU_KG' | 'PRIX_FIXE' | 'FORMATS' | 'COLIS';
export type ModeQuantite = 'POIDS' | 'PERSONNES' | 'PIECE';

export interface Format {
  libelle: string;
  poidsG: number;
  prix: number;
  prixKg: number | null;
}

export interface Portion {
  minG: number;
  maxG: number;
  pasG: number;
  gParPersonne: number;
  modeDefaut: ModeQuantite;
  poidsReferenceG: number | null;
}

export interface Photo {
  url: string;
  alt: string;
  legende: string;
  credit: string;
}

export interface OptionProduit {
  cle: string;
  libelle: string;
  valeurs: string[];
}

export interface Produit {
  id: string;
  slug: string;
  nom: string;
  categorieId: string;
  famille: string;
  familleNom: string;
  modele: Modele;
  prixKg: number | null;
  prixFixe: number | null;
  poidsG: number | null;
  formats: Format[];
  /** Clé de la règle de portion publiée (A_GRILLER, COLIS_8…) — jamais réinventée ici. */
  portionRule: string | null;
  portion: Portion;
  race: string | null;
  raceOrigine: string | null;
  bms: string | null;
  maturationJours: number | null;
  maturationMethode: string | null;
  labels: string[];
  halal: boolean;
  origine: string;
  origineDetail: { ne: string | null; eleve: string | null; abattu: string | null; decoupe: string | null };
  espece: string;
  especes: string[];
  description: string;
  conseil: string;
  conservation: string | null;
  cuisson: string[];
  temperatures: Record<string, number> | null;
  reposMin: number | null;
  allergenes: string[];
  ingredients: string | null;
  composition: Array<{ name?: string; nom?: string; weightG?: number; poidsG?: number }>;
  options: OptionProduit[];
  tags: string[];
  photos: Photo[];
  photo: string | null;
  vignette: string | null;
  abattageMaison: boolean;
  disponibleEn: string[];
  indisponibleEn: string[];
}

export interface Famille {
  slug: string;
  chemin: string;
  nom: string;
  accroche: string;
  surtitre: string;
  texte: string;
  nombre: number;
  aPartirDe: number | null;
  teinte: string;
}

export interface Boutique {
  id: string;
  nom: string;
  adresse: string;
  telephone: string;
  horaires: Array<{ jour: string; heures: string }>;
  specialites: string[];
  parking: string | null;
  responsable: string | null;
  retrait: boolean;
  halalOnly: boolean;
  especesExclues: string[];
}

export interface Avis {
  id: string;
  auteur: string;
  ville: string;
  note: number;
  date: string;
  produit: string;
  titre: string;
  texte: string;
}

export interface Question {
  question: string;
  reponse: string;
}

export interface Etape {
  cle: string;
  titre: string;
  texte: string;
}

export interface Valeur {
  titre: string;
  texte: string;
}

export interface Jalon {
  annee: string;
  titre: string;
  texte: string;
}

export interface LigneCommande {
  nom: string;
  quantite: number;
  prix: number;
  /** Détail de pesée : ce que le client voit dans « Mes commandes ». */
  options: string;
  poidsEstimeG: number | null;
  poidsReelG: number | null;
  ecartPct: number | null;
  prixKg: number | null;
  prixFinal: number | null;
  pese: boolean;
}

export interface EtapeSuivi {
  titre: string;
  fait: boolean;
  date: string;
}

export interface Commande {
  numero: string;
  date: string;
  mode: string;
  modeLisible: string;
  statut: string;
  statutLisible: string;
  creneau: string;
  boutiqueId: string;
  clientNom: string;
  clientTelephone: string;
  total: number;
  arrhes: number;
  solde: number;
  paye: number;
  totalFinal: number | null;
  lignes: LigneCommande[];
  suivi: EtapeSuivi[];
}

export interface Compte {
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  depuis: string;
  tier: string;
  fidelite: { palier: string; points: number; prochain: number };
  adresses: Array<{
    id: string; libelle: string; defaut: boolean; ligne: string;
    complement: string; codePostal: string; ville: string; cp: string;
  }>;
  boutiqueFavorite: string;
  preferences: { mode: string; courriel: boolean; sms: boolean; coupures: string };
}

/** [RÉGLAGE] Toute valeur commerciale affichée vient d'ici — jamais d'un composant. */
export const REGLAGES = {
  "marque": {
    "nom": "Maison Duval",
    "baseline": "Boucherie · Charcuterie · Volailles",
    "depuis": 1922,
    "telephone": "01 39 XX XX XX",
    "email": "contact@maison-duval.fr",
    "note": 4.8,
    "avis": 8
  },
  "livraison": {
    "seuilGratuite": 99.0,
    "fraisChronofresh": 9.9,
    "fraisCoursier": 6.9,
    "seuilCoursierGratuit": 149.0,
    "zonesCoursier": [
      "95120",
      "95130",
      "95100",
      "95210"
    ],
    "coupure": "12:00",
    "delaiMinH": 24,
    "delaiMaxH": 48,
    "reclamationFroidH": 4,
    "joursExpedition": [
      "Mardi",
      "Mercredi",
      "Jeudi",
      "Vendredi",
      "Samedi"
    ],
    "temperature": "0–4 °C",
    "transporteur": "Chronofresh"
  },
  "poids": {
    "tolerancePct": 5,
    "grossePieceG": 1500,
    "auDela": "CAP"
  },
  "arrhes": {
    "seuilLibre": 50.0,
    "seuilHaut": 120.0,
    "pctStandard": 20,
    "plancherStandard": 10.0,
    "pctMesure": 30,
    "pctException": 50,
    "plancherPointePct": 30,
    "arrondi": "UP_TO_EURO",
    "livraisonIntegrale": true,
    "horizonBancaireJours": 6,
    "regleStandard": "R3b",
    "regleMesure": "R1",
    "reglePointe": "R5",
    "version": "rv_6"
  },
  "creneaux": {
    "granulariteMin": 30,
    "joursOuverts": 14,
    "maxCommandes": 12,
    "delaiDecoupeH": 24,
    "delaiMaturageH": 48,
    "delaiAgneauEntierH": 72
  },
  "delais": {
    "annulationH": 24,
    "conservationJours": 4
  },
  "rgpd": {
    "suppressionJours": 30
  },
  "tva": {
    "alimentaire": 5.5,
    "accessoire": 20.0
  },
  "confort": {
    "echelle": 1.15
  }
};

export const BOUTIQUES: Boutique[] = [
  {
    "id": "store_sannois",
    "nom": "Sannois",
    "adresse": "17 rue d'Argenteuil, 95120 Sannois",
    "telephone": "01 39 XX XX XX",
    "horaires": [
      {
        "jour": "Lundi",
        "heures": "Fermé"
      },
      {
        "jour": "Mardi",
        "heures": "8h30–13h00 / 15h00–19h30"
      },
      {
        "jour": "Mercredi",
        "heures": "8h30–13h00 / 15h00–19h30"
      },
      {
        "jour": "Jeudi",
        "heures": "8h30–13h00 / 15h00–19h30"
      },
      {
        "jour": "Vendredi",
        "heures": "8h30–13h00 / 15h00–20h00"
      },
      {
        "jour": "Samedi",
        "heures": "8h00–19h30"
      },
      {
        "jour": "Dimanche",
        "heures": "Fermé"
      }
    ],
    "specialites": [
      "Triperie complète",
      "Cabinet de maturation",
      "Comptoir retrait à droite en entrant"
    ],
    "parking": "Parking Mairie, 2 min à pied",
    "responsable": "Sonia",
    "retrait": true,
    "halalOnly": false,
    "especesExclues": []
  },
  {
    "id": "store_franconville",
    "nom": "Franconville",
    "adresse": "12 rue du Général Leclerc, 95130 Franconville",
    "telephone": "01 34 XX XX XX",
    "horaires": [
      {
        "jour": "Lundi",
        "heures": "Fermé"
      },
      {
        "jour": "Mardi",
        "heures": "9h00–13h00 / 15h00–19h30"
      },
      {
        "jour": "Mercredi",
        "heures": "9h00–13h00 / 15h00–19h30"
      },
      {
        "jour": "Jeudi",
        "heures": "9h00–13h00 / 15h00–19h30"
      },
      {
        "jour": "Vendredi",
        "heures": "9h00–13h00 / 15h00–20h00"
      },
      {
        "jour": "Samedi",
        "heures": "8h30–19h00"
      },
      {
        "jour": "Dimanche",
        "heures": "Fermé"
      }
    ],
    "specialites": [
      "Charcuterie maison",
      "Colis famille"
    ],
    "parking": "Devant la boutique (10 min gratuites)",
    "responsable": "Laurent",
    "retrait": true,
    "halalOnly": true,
    "especesExclues": [
      "CANARD",
      "SANGLIER"
    ]
  },
  {
    "id": "store_argenteuil",
    "nom": "Argenteuil",
    "adresse": "48 avenue Jean Jaurès, 95100 Argenteuil",
    "telephone": "01 39 YY YY YY",
    "horaires": [
      {
        "jour": "Lundi",
        "heures": "Fermé"
      },
      {
        "jour": "Mardi",
        "heures": "8h30–13h00 / 15h00–19h00"
      },
      {
        "jour": "Mercredi",
        "heures": "8h30–13h00 / 15h00–19h00"
      },
      {
        "jour": "Jeudi",
        "heures": "8h30–13h00 / 15h00–19h00"
      },
      {
        "jour": "Vendredi",
        "heures": "8h30–13h00 / 15h00–20h00"
      },
      {
        "jour": "Samedi",
        "heures": "8h00–19h00"
      },
      {
        "jour": "Dimanche",
        "heures": "Fermé"
      }
    ],
    "specialites": [
      "Volailles de notre abattoir",
      "Préparations pour barbecue"
    ],
    "parking": "Parking Centre-Ville",
    "responsable": "Fabrice",
    "retrait": true,
    "halalOnly": true,
    "especesExclues": [
      "CANARD",
      "SANGLIER"
    ]
  }
];

export const FAMILLES: Famille[] = [
  {
    "slug": "boeuf",
    "chemin": "boutique/boeuf",
    "nom": "Bœuf & Génisse",
    "accroche": "Maturé sur os",
    "surtitre": "Maturé sur os",
    "texte": "Charolaise, Angus, Wagyu : un cabinet, jamais de vide.",
    "nombre": 6,
    "aPartirDe": 17.9,
    "teinte": "wine"
  },
  {
    "slug": "veau",
    "chemin": "boutique/veau",
    "nom": "Veau",
    "accroche": "Rôti du dimanche",
    "surtitre": "Rôti du dimanche",
    "texte": "Veau de Corrèze, ficelé main, chair fine.",
    "nombre": 1,
    "aPartirDe": 32.9,
    "teinte": "blush"
  },
  {
    "slug": "agneau",
    "chemin": "boutique/agneau",
    "nom": "Agneau",
    "accroche": "Gigot & côtes",
    "surtitre": "Gigot & côtes",
    "texte": "Agneau de France. Halal sur deux boutiques.",
    "nombre": 1,
    "aPartirDe": 26.9,
    "teinte": "sand"
  },
  {
    "slug": "canard-charcuterie",
    "chemin": "boutique/canard-charcuterie",
    "nom": "Canard & Charcuterie",
    "accroche": "Fermier & maison",
    "surtitre": "Fermier & maison",
    "texte": "Canard fermier, saucisses et boudins faits chaque semaine.",
    "nombre": 1,
    "aPartirDe": 13.9,
    "teinte": "clay"
  },
  {
    "slug": "volailles",
    "chemin": "boutique/volailles",
    "nom": "Volailles de notre abattoir",
    "accroche": "Abattues chez nous",
    "surtitre": "Abattues chez nous",
    "texte": "Plein air, atelier de Sannois, mardi et vendredi.",
    "nombre": 1,
    "aPartirDe": 14.31,
    "teinte": "olive"
  },
  {
    "slug": "preparations-maison",
    "chemin": "boutique/preparations-maison",
    "nom": "Préparations Maison",
    "accroche": "Merguez & chipolata",
    "surtitre": "Merguez & chipolata",
    "texte": "Le laboratoire : marinades, brochettes, boudin noir.",
    "nombre": 3,
    "aPartirDe": 14.99,
    "teinte": "wine"
  },
  {
    "slug": "mechoui-receptions",
    "chemin": "boutique/mechoui-receptions",
    "nom": "Méchoui & Réceptions",
    "accroche": "Pour 8 à 30",
    "surtitre": "Pour 8 à 30",
    "texte": "Agneaux marinés et mis en broche, commandés 72 h avant.",
    "nombre": 4,
    "aPartirDe": 9.99,
    "teinte": "clay"
  },
  {
    "slug": "colis-coffrets",
    "chemin": "boutique/colis-coffrets",
    "nom": "Colis & Coffrets",
    "accroche": "Prêts à griller",
    "surtitre": "Prêts à griller",
    "texte": "Composés par le boucher, à emporter ou à offrir.",
    "nombre": 1,
    "aPartirDe": 37.46,
    "teinte": "sand"
  },
  {
    "slug": "triperie-abats",
    "chemin": "boutique/triperie-abats",
    "nom": "Triperie / Abats",
    "accroche": "Os à moelle & boudin",
    "surtitre": "Os à moelle & boudin",
    "texte": "Une tradition qui ne se perd pas chez nous.",
    "nombre": 1,
    "aPartirDe": 16.5,
    "teinte": "slate"
  },
  {
    "slug": "epicerie-fine",
    "chemin": "boutique/epicerie-fine",
    "nom": "Épicerie fine & Accompagnements",
    "accroche": "Accompagnements",
    "surtitre": "Accompagnements",
    "texte": "Pommes de terre, condiments, ce qui va avec.",
    "nombre": 1,
    "aPartirDe": 2.8,
    "teinte": "olive"
  }
];

export const PRODUITS: Produit[] = [
  {
    "id": "p_cote_boeuf_28",
    "slug": "cote-de-boeuf-maturee-28-jours",
    "nom": "Côte de bœuf maturée 28 jours",
    "categorieId": "cat_boeuf",
    "famille": "boeuf",
    "familleNom": "Bœuf & Génisse",
    "modele": "AU_KG",
    "prixKg": 49.9,
    "prixFixe": null,
    "poidsG": null,
    "formats": [],
    "portionRule": "COTE_DE_BOEUF",
    "portion": {
      "minG": 600,
      "maxG": 2500,
      "pasG": 100,
      "gParPersonne": 300,
      "modeDefaut": "PERSONNES",
      "poidsReferenceG": 1200
    },
    "race": "Charolaise",
    "raceOrigine": "France",
    "bms": "6",
    "maturationJours": 28,
    "maturationMethode": "Sur os, cabinet de maturation maison",
    "labels": [
      "Viande Bovine Française",
      "Bleu-Blanc-Cœur"
    ],
    "halal": false,
    "origine": "Né et élevé en France · Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": "France",
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "BOVIN",
    "especes": [
      "BOVIN"
    ],
    "description": "Charolaise maturée vingt-huit jours sur os. Persillée, une croûte fine, un goût de noisette qui s’installe en fin de bouche.",
    "conseil": "Sortez la pièce du froid trente minutes avant de la saisir, salez au dernier moment, et laissez-la reposer après cuisson : c’est là que le jus se réinstalle.",
    "conservation": "3 jours au réfrigérateur entre 0 et 4 °C · congélation 3 mois",
    "cuisson": [
      "Braise",
      "Plancha",
      "Four"
    ],
    "temperatures": {
      "saignant": 54,
      "aPoint": 60
    },
    "reposMin": 5,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [
      {
        "cle": "EPAISSEUR",
        "libelle": "Épaisseur de coupe",
        "valeurs": [
          "2 cm",
          "3 cm",
          "4 cm"
        ]
      },
      {
        "cle": "PREPARATION",
        "libelle": "Préparation",
        "valeurs": [
          "Parée",
          "Ficelée",
          "Désossée"
        ]
      }
    ],
    "tags": [
      "a_partager",
      "signature"
    ],
    "photos": [
      {
        "url": "https://images.unsplash.com/photo-1551028150-64b9f398f678?auto=format&fit=crop&w=1200&q=72",
        "alt": "Côte de bœuf crue posée sur de la glace, couteau de boucherie à proximité",
        "legende": "Cru — fond blanc pur",
        "credit": "Unsplash · Jason Leung"
      },
      {
        "url": "https://images.unsplash.com/photo-1560781290-7dc94c0f8f4f?auto=format&fit=crop&w=1200&q=72",
        "alt": "Viande crue déposée sur une planche de bois avec du romarin",
        "legende": "Cru — planche en bois",
        "credit": "Unsplash · Jason Leung"
      },
      {
        "url": "https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?auto=format&fit=crop&w=1200&q=72",
        "alt": "Rôti tranché, cuisson rosée",
        "legende": "Cuit — suggestion de service",
        "credit": "Unsplash · Amirali Mirhashemian"
      }
    ],
    "photo": "https://images.unsplash.com/photo-1551028150-64b9f398f678?auto=format&fit=crop&w=1200&q=72",
    "vignette": "https://images.unsplash.com/photo-1551028150-64b9f398f678?auto=format&fit=crop&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_entrecote_angus",
    "slug": "entrecote",
    "nom": "Entrecôte Black Angus",
    "categorieId": "cat_boeuf",
    "famille": "boeuf",
    "familleNom": "Bœuf & Génisse",
    "modele": "AU_KG",
    "prixKg": 54.4,
    "prixFixe": null,
    "poidsG": null,
    "formats": [],
    "portionRule": "A_GRILLER",
    "portion": {
      "minG": 150,
      "maxG": 1500,
      "pasG": 50,
      "gParPersonne": 200,
      "modeDefaut": "PERSONNES",
      "poidsReferenceG": null
    },
    "race": "Black Angus",
    "raceOrigine": "Irlande",
    "bms": "7",
    "maturationJours": 35,
    "maturationMethode": null,
    "labels": [
      "Angus certifié"
    ],
    "halal": false,
    "origine": "Né en Irlande · Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": "Irlande",
      "eleve": "Irlande",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "BOVIN",
    "especes": [
      "BOVIN"
    ],
    "description": "Black Angus élevé en Irlande, maturé trente-cinq jours. Une entrecôte fine, joliment persillée, qui reste moelleuse à cœur.",
    "conseil": "Sortez la pièce du froid trente minutes avant de la saisir, salez au dernier moment, et laissez-la reposer après cuisson : c’est là que le jus se réinstalle.",
    "conservation": "3 jours au réfrigérateur entre 0 et 4 °C",
    "cuisson": [
      "Braise",
      "Plancha"
    ],
    "temperatures": {
      "bleu": 48,
      "saignant": 54,
      "aPoint": 60
    },
    "reposMin": 5,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [
      {
        "cle": "EPAISSEUR",
        "libelle": "Épaisseur de coupe",
        "valeurs": [
          "2 cm",
          "3 cm",
          "4 cm"
        ]
      }
    ],
    "tags": [],
    "photos": [
      {
        "url": "https://images.unsplash.com/photo-1603360946369-dc9bb6258143?auto=format&fit=crop&w=1200&q=72",
        "alt": "Entrecôte crue entourée de gros sel sur un fond sombre",
        "legende": "Cru — fond blanc pur",
        "credit": "Unsplash · Jason Leung"
      },
      {
        "url": "https://images.unsplash.com/photo-1615937691194-97dbd3f3dc29?auto=format&fit=crop&w=1200&q=72",
        "alt": "Pièces de bœuf crues présentées sur du papier de boucherie",
        "legende": "Cru — planche en bois",
        "credit": "Unsplash · Nick Karvounis"
      },
      {
        "url": "https://images.unsplash.com/photo-1558030006-450675393462?auto=format&fit=crop&w=1200&q=72",
        "alt": "Tranches de rôti disposées sur une planche",
        "legende": "Cuit — suggestion de service",
        "credit": "Unsplash · Sebastian Coman Photography"
      }
    ],
    "photo": "https://images.unsplash.com/photo-1603360946369-dc9bb6258143?auto=format&fit=crop&w=1200&q=72",
    "vignette": "https://images.unsplash.com/photo-1603360946369-dc9bb6258143?auto=format&fit=crop&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_roti_veau",
    "slug": "roti-de-veau-mechoui",
    "nom": "Rôti de veau",
    "categorieId": "cat_veau",
    "famille": "veau",
    "familleNom": "Veau",
    "modele": "AU_KG",
    "prixKg": 32.9,
    "prixFixe": null,
    "poidsG": null,
    "formats": [],
    "portionRule": "ROTI_DESOSSE",
    "portion": {
      "minG": 500,
      "maxG": 4000,
      "pasG": 100,
      "gParPersonne": 190,
      "modeDefaut": "PERSONNES",
      "poidsReferenceG": null
    },
    "race": "Veau de Corrèze",
    "raceOrigine": "France",
    "bms": "3",
    "maturationJours": 0,
    "maturationMethode": null,
    "labels": [
      "Veau de France"
    ],
    "halal": false,
    "origine": "Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": null,
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "BOVIN_VEAU",
    "especes": [
      "BOVIN_VEAU"
    ],
    "description": "Veau de Corrèze, rôti ficelé à la main. Chair fine, jus clair : le déjeuner de famille tranché épais, sans chichi.",
    "conseil": "Un rôti de veau se cuit à 160 °C sans être piqué. Laissez-le reposer dix minutes sous papier aluminium avant de le trancher.",
    "conservation": "3 jours au réfrigérateur entre 0 et 4 °C",
    "cuisson": [
      "Four"
    ],
    "temperatures": {
      "rosé": 60,
      "aPoint": 65
    },
    "reposMin": 10,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [
      {
        "cle": "PREPARATION",
        "libelle": "Préparation",
        "valeurs": [
          "Ficelée",
          "Bardée"
        ]
      },
      {
        "cle": "EMBALLAGE",
        "libelle": "Emballage",
        "valeurs": [
          "Traditionnel",
          "Sous vide par portion"
        ]
      }
    ],
    "tags": [],
    "photos": [],
    "photo": null,
    "vignette": null,
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_merguez_maison",
    "slug": "merguez-maison",
    "nom": "Merguez maison",
    "categorieId": "cat_preparations",
    "famille": "preparations-maison",
    "familleNom": "Préparations Maison",
    "modele": "FORMATS",
    "prixKg": 18.75,
    "prixFixe": null,
    "poidsG": null,
    "formats": [
      {
        "libelle": "500 g",
        "poidsG": 500,
        "prix": 9.99,
        "prixKg": 19.98
      },
      {
        "libelle": "1 kg",
        "poidsG": 1000,
        "prix": 18.75,
        "prixKg": 18.75
      },
      {
        "libelle": "2 kg",
        "poidsG": 2000,
        "prix": 35,
        "prixKg": 17.5
      }
    ],
    "portionRule": "HACHE_SAUCISSE",
    "portion": {
      "minG": 250,
      "maxG": 3000,
      "pasG": 50,
      "gParPersonne": 175,
      "modeDefaut": "POIDS",
      "poidsReferenceG": 500
    },
    "race": null,
    "raceOrigine": null,
    "bms": null,
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [
      "Fait maison"
    ],
    "halal": false,
    "origine": "Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": null,
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "BOVIN_OVIN_CANARD",
    "especes": [
      "BOVIN",
      "OVIN",
      "CANARD"
    ],
    "description": "Bœuf et agneau, harissa douce, cumin et coriandre. Aucun additif, boyau naturel de mouton : la merguez de la maison.",
    "conseil": "Nos préparations ne contiennent aucun additif : cuisez-les à feu doux, elles rendent plus de gras et ne se dessèchent pas.",
    "conservation": "2 jours au réfrigérateur entre 0 et 4 °C · à consommer rapidement",
    "cuisson": [
      "Braise",
      "Plancha",
      "Four"
    ],
    "temperatures": {
      "aPoint": 71
    },
    "reposMin": 2,
    "allergenes": [
      "moutarde",
      "sulfites"
    ],
    "ingredients": "Viande de bœuf et d'agneau, gras de bœuf, épices, sel, boyau naturel de mouton, arôme naturel.",
    "composition": [],
    "options": [],
    "tags": [
      "barbecue",
      "fait_maison"
    ],
    "photos": [
      {
        "url": "https://commons.wikimedia.org/wiki/Special:FilePath/Saucisse_de_Toulouse.jpg?width=1200",
        "alt": "Saucisses crues fraîchement préparées",
        "legende": "Cru — fond blanc pur",
        "credit": "Wikimedia Commons · Slastic"
      },
      {
        "url": "https://commons.wikimedia.org/wiki/Special:FilePath/Barbecue%2C_merguez%2C_saucisses_et_brochettes.JPG?width=1200",
        "alt": "Merguez et saucisses en fin de cuisson sur le grill",
        "legende": "Cuit — suggestion de service",
        "credit": "Wikimedia Commons · Benoît Prieur"
      }
    ],
    "photo": "https://commons.wikimedia.org/wiki/Special:FilePath/Saucisse_de_Toulouse.jpg?width=1200",
    "vignette": "https://commons.wikimedia.org/wiki/Special:FilePath/Saucisse_de_Toulouse.jpg?width=1200&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois"
    ],
    "indisponibleEn": [
      "store_franconville",
      "store_argenteuil"
    ]
  },
  {
    "id": "p_poulet_fermier",
    "slug": "poulet-fermier-entier",
    "nom": "Poulet fermier entier",
    "categorieId": "cat_volailles",
    "famille": "volailles",
    "familleNom": "Volailles de notre abattoir",
    "modele": "AU_KG",
    "prixKg": 14.31,
    "prixFixe": null,
    "poidsG": null,
    "formats": [],
    "portionRule": "VOLAILLE_ENTIERE",
    "portion": {
      "minG": 1200,
      "maxG": 4000,
      "pasG": 100,
      "gParPersonne": 375,
      "modeDefaut": "PIECE",
      "poidsReferenceG": null
    },
    "race": "Poulet fermier Label Rouge",
    "raceOrigine": "France",
    "bms": null,
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [
      "Label Rouge"
    ],
    "halal": false,
    "origine": "Découpe à Sannois",
    "origineDetail": {
      "ne": null,
      "eleve": "France",
      "abattu": "France (notre abattoir, Sannois)",
      "decoupe": "France (Sannois)"
    },
    "espece": "VOLAILLE",
    "especes": [
      "VOLAILLE"
    ],
    "description": "Volaille fermière élevée en plein air et abattue dans notre atelier de Sannois. Peau fine, chair ferme, goût de volaille d’autrefois.",
    "conseil": "Notre volaille fermière a la chair ferme : comptez vingt minutes de plus qu’un poulet industriel, et piquez la cuisse pour juger.",
    "conservation": "3 jours au réfrigérateur entre 0 et 4 °C",
    "cuisson": [
      "Four",
      "Rôtissoire"
    ],
    "temperatures": {
      "aPoint": 74
    },
    "reposMin": 10,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [
      {
        "cle": "PREPARATION",
        "libelle": "Préparation",
        "valeurs": [
          "Entier",
          "Découpé en 8 morceaux",
          "Prêt à rôtir (ficelé)"
        ]
      }
    ],
    "tags": [
      "abattoir_maison"
    ],
    "photos": [],
    "photo": null,
    "vignette": null,
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_cote_wagyu",
    "slug": "cote-de-boeuf",
    "nom": "Côte de bœuf Wagyu croisé",
    "categorieId": "cat_boeuf",
    "famille": "boeuf",
    "familleNom": "Bœuf & Génisse",
    "modele": "AU_KG",
    "prixKg": 150,
    "prixFixe": null,
    "poidsG": null,
    "formats": [],
    "portionRule": "COTE_DE_BOEUF",
    "portion": {
      "minG": 600,
      "maxG": 2500,
      "pasG": 100,
      "gParPersonne": 300,
      "modeDefaut": "PERSONNES",
      "poidsReferenceG": 1200
    },
    "race": "Wagyu croisé",
    "raceOrigine": "France",
    "bms": "9",
    "maturationJours": 45,
    "maturationMethode": "Dry aged 45 jours en cabinet",
    "labels": [
      "Wagyu croisé"
    ],
    "halal": false,
    "origine": "Né et élevé en France · Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": "France",
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "BOVIN",
    "especes": [
      "BOVIN"
    ],
    "description": "Wagyu croisé, persillé BMS 8 à 9, maturé quarante-cinq jours. La pièce d’exception : à saisir très vite, à partager sans hâte.",
    "conseil": "Sortez la pièce du froid trente minutes avant de la saisir, salez au dernier moment, et laissez-la reposer après cuisson : c’est là que le jus se réinstalle.",
    "conservation": "3 jours au réfrigérateur entre 0 et 4 °C",
    "cuisson": [
      "Braise"
    ],
    "temperatures": {
      "saignant": 54
    },
    "reposMin": 8,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [],
    "tags": [
      "wagyu",
      "exception"
    ],
    "photos": [
      {
        "url": "https://images.unsplash.com/photo-1551028150-64b9f398f678?auto=format&fit=crop&w=1200&q=72",
        "alt": "Côte de bœuf crue sur glace, mise en scène de boucherie",
        "legende": "Cru — planche en bois",
        "credit": "Unsplash · Jason Leung"
      },
      {
        "url": "https://images.unsplash.com/photo-1615937657715-bc7b4b7962c1?auto=format&fit=crop&w=1200&q=72",
        "alt": "Tomahawk saisi en cocotte, pommes de terre et romarin",
        "legende": "Cuit — suggestion de service",
        "credit": "Unsplash · Emerson Vieira"
      }
    ],
    "photo": "https://images.unsplash.com/photo-1551028150-64b9f398f678?auto=format&fit=crop&w=1200&q=72",
    "vignette": "https://images.unsplash.com/photo-1551028150-64b9f398f678?auto=format&fit=crop&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_agneau_entier_mechoui",
    "slug": "agneau-entier-mechoui",
    "nom": "Agneau entier méchoui",
    "categorieId": "cat_mechoui",
    "famille": "mechoui-receptions",
    "familleNom": "Méchoui & Réceptions",
    "modele": "PRIX_FIXE",
    "prixKg": null,
    "prixFixe": 289,
    "poidsG": null,
    "formats": [],
    "portionRule": "MECHOUI_ENTIER",
    "portion": {
      "minG": 14000,
      "maxG": 24000,
      "pasG": 500,
      "gParPersonne": 550,
      "modeDefaut": "PIECE",
      "poidsReferenceG": null
    },
    "race": null,
    "raceOrigine": null,
    "bms": null,
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [
      "Halal",
      "Agneau de France"
    ],
    "halal": true,
    "origine": "Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": null,
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "OVIN",
    "especes": [
      "OVIN"
    ],
    "description": "Agneau entier préparé et mariné, seize à vingt kilos, pour trente convives. Découpe et mise en broche comprises.",
    "conseil": "Commandez soixante-douze heures à l’avance. Nous préparons, marinons et mettons en broche le jour même ; vous n’avez qu’à surveiller le feu.",
    "conservation": null,
    "cuisson": [],
    "temperatures": null,
    "reposMin": null,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [
      {
        "cle": "PREPARATION",
        "libelle": "Préparation",
        "valeurs": [
          "Entier",
          "Découpé en 8 morceaux",
          "En quartiers pour rôtissoire"
        ]
      }
    ],
    "tags": [
      "evenement",
      "mechoui"
    ],
    "photos": [
      {
        "url": "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=1200&q=72",
        "alt": "Pièces crues préparées pour un repas de fête",
        "legende": "Entier cru",
        "credit": "Unsplash · Jason Leung"
      },
      {
        "url": "https://commons.wikimedia.org/wiki/Special:FilePath/Mechoui.jpg?width=1200",
        "alt": "Agneau entier rôti à la broche, prêt à être découpé",
        "legende": "Sur broche, cuit",
        "credit": "Wikimedia Commons · Andre Piquet"
      }
    ],
    "photo": "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=1200&q=72",
    "vignette": "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_agneau_demi_mechoui",
    "slug": "demi-agneau-mechoui",
    "nom": "Demi-agneau méchoui",
    "categorieId": "cat_mechoui",
    "famille": "mechoui-receptions",
    "familleNom": "Méchoui & Réceptions",
    "modele": "PRIX_FIXE",
    "prixKg": null,
    "prixFixe": 189,
    "poidsG": null,
    "formats": [],
    "portionRule": "MECHOUI_DEMI",
    "portion": {
      "minG": 7000,
      "maxG": 12000,
      "pasG": 500,
      "gParPersonne": 550,
      "modeDefaut": "PIECE",
      "poidsReferenceG": null
    },
    "race": null,
    "raceOrigine": null,
    "bms": null,
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [],
    "halal": false,
    "origine": "Né et élevé en France · Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": "France",
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "OVIN",
    "especes": [
      "OVIN"
    ],
    "description": "Demi-agneau mariné vingt-quatre heures aux herbes du Roussillon. Pour quinze convives, sans se tromper sur la quantité.",
    "conseil": "Commandez soixante-douze heures à l’avance. Nous préparons, marinons et mettons en broche le jour même ; vous n’avez qu’à surveiller le feu.",
    "conservation": null,
    "cuisson": [],
    "temperatures": null,
    "reposMin": null,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [],
    "tags": [],
    "photos": [
      {
        "url": "https://commons.wikimedia.org/wiki/Special:FilePath/Gigot_d_agneau_2019.jpg?width=1200",
        "alt": "Gigot d'agneau rôti, doré à cœur",
        "legende": "Sur broche, cuit",
        "credit": "Wikimedia Commons · Cloudapple"
      }
    ],
    "photo": "https://commons.wikimedia.org/wiki/Special:FilePath/Gigot_d_agneau_2019.jpg?width=1200",
    "vignette": "https://commons.wikimedia.org/wiki/Special:FilePath/Gigot_d_agneau_2019.jpg?width=1200&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_agneau_quart_mechoui",
    "slug": "quart-agneau-mechoui",
    "nom": "Quart d'agneau méchoui",
    "categorieId": "cat_mechoui",
    "famille": "mechoui-receptions",
    "familleNom": "Méchoui & Réceptions",
    "modele": "PRIX_FIXE",
    "prixKg": null,
    "prixFixe": 149,
    "poidsG": null,
    "formats": [],
    "portionRule": "MECHOUI_QUART",
    "portion": {
      "minG": 3500,
      "maxG": 6000,
      "pasG": 250,
      "gParPersonne": 550,
      "modeDefaut": "PIECE",
      "poidsReferenceG": null
    },
    "race": null,
    "raceOrigine": null,
    "bms": null,
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [],
    "halal": false,
    "origine": "Né et élevé en France · Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": "France",
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "OVIN",
    "especes": [
      "OVIN"
    ],
    "description": "Quart d’agneau pour une table de sept à huit. La formule des grandes occasions, sans le surplus.",
    "conseil": "Commandez soixante-douze heures à l’avance. Nous préparons, marinons et mettons en broche le jour même ; vous n’avez qu’à surveiller le feu.",
    "conservation": null,
    "cuisson": [],
    "temperatures": null,
    "reposMin": null,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [],
    "tags": [],
    "photos": [
      {
        "url": "https://commons.wikimedia.org/wiki/Special:FilePath/Gigot_d_agneau_2019.jpg?width=1200",
        "alt": "Gigot d'agneau rôti, doré à cœur",
        "legende": "Sur broche, cuit",
        "credit": "Wikimedia Commons · Cloudapple"
      }
    ],
    "photo": "https://commons.wikimedia.org/wiki/Special:FilePath/Gigot_d_agneau_2019.jpg?width=1200",
    "vignette": "https://commons.wikimedia.org/wiki/Special:FilePath/Gigot_d_agneau_2019.jpg?width=1200&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_pommes_de_terre_mechoui",
    "slug": "pommes-de-terre-mechoui",
    "nom": "Pommes de terre méchoui",
    "categorieId": "cat_epicerie",
    "famille": "epicerie-fine",
    "familleNom": "Épicerie fine & Accompagnements",
    "modele": "PRIX_FIXE",
    "prixKg": 2.8,
    "prixFixe": 6.99,
    "poidsG": null,
    "formats": [],
    "portionRule": "ACCOMPAGNEMENT",
    "portion": {
      "minG": 500,
      "maxG": 3000,
      "pasG": 250,
      "gParPersonne": 200,
      "modeDefaut": "PIECE",
      "poidsReferenceG": 1000
    },
    "race": null,
    "raceOrigine": null,
    "bms": null,
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [],
    "halal": false,
    "origine": "Découpe à Sannois",
    "origineDetail": {
      "ne": null,
      "eleve": "France",
      "abattu": null,
      "decoupe": "France (Val-d'Oise)"
    },
    "espece": "AUTRE",
    "especes": [],
    "description": "Grenaille à rôtir dans le jus de la broche. L’accompagnement qui ne se discute pas dans un méchoui.",
    "conseil": "Un tour de moulin, un filet d’huile : ces pommes de terre n’ont besoin de rien d’autre pour accompagner une broche.",
    "conservation": null,
    "cuisson": [],
    "temperatures": null,
    "reposMin": null,
    "allergenes": [],
    "ingredients": "Pommes de terre, huile, paprika, thym, sel.",
    "composition": [],
    "options": [],
    "tags": [],
    "photos": [],
    "photo": null,
    "vignette": null,
    "abattageMaison": false,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_bourguignon",
    "slug": "boeuf-bourguignon",
    "nom": "Bœuf bourguignon (morceaux à mijoter)",
    "categorieId": "cat_boeuf",
    "famille": "boeuf",
    "familleNom": "Bœuf & Génisse",
    "modele": "AU_KG",
    "prixKg": 19.9,
    "prixFixe": null,
    "poidsG": null,
    "formats": [],
    "portionRule": "A_MIJOTER",
    "portion": {
      "minG": 500,
      "maxG": 5000,
      "pasG": 100,
      "gParPersonne": 275,
      "modeDefaut": "PERSONNES",
      "poidsReferenceG": null
    },
    "race": "Charolaise",
    "raceOrigine": "France",
    "bms": "5–6",
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [],
    "halal": false,
    "origine": "Né et élevé en France · Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": "France",
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "BOVIN",
    "especes": [
      "BOVIN"
    ],
    "description": "Joue et paleron taillés pour mijoter — des morceaux qui tiennent à la cuisson et qui gélatinisent lentement.",
    "conseil": "Sortez la pièce du froid trente minutes avant de la saisir, salez au dernier moment, et laissez-la reposer après cuisson : c’est là que le jus se réinstalle.",
    "conservation": null,
    "cuisson": [
      "Cocotte"
    ],
    "temperatures": {
      "aPoint": 85
    },
    "reposMin": null,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [
      {
        "cle": "PREPARATION",
        "libelle": "Préparation",
        "valeurs": [
          "En cubes 3 cm",
          "En cubes 5 cm",
          "Mariné au vin rouge (+24 h)"
        ]
      }
    ],
    "tags": [
      "a_mijoter",
      "recette"
    ],
    "photos": [
      {
        "url": "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=1200&q=72",
        "alt": "Assortiment de viandes crues avec herbes et épices",
        "legende": "Cru — fond blanc pur",
        "credit": "Unsplash · Jason Leung"
      },
      {
        "url": "https://images.unsplash.com/photo-1611270629569-8b357cb88da9?auto=format&fit=crop&w=1200&q=72",
        "alt": "Plat mijoté servi avec des pâtes fraîches",
        "legende": "Cuit — suggestion de service",
        "credit": "Unsplash · Svitlana"
      }
    ],
    "photo": "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=1200&q=72",
    "vignette": "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_brochettes_poulet",
    "slug": "brochettes-poulet",
    "nom": "Brochettes de poulet marinées",
    "categorieId": "cat_preparations",
    "famille": "preparations-maison",
    "familleNom": "Préparations Maison",
    "modele": "FORMATS",
    "prixKg": 18.59,
    "prixFixe": null,
    "poidsG": null,
    "formats": [
      {
        "libelle": "6 brochettes (≈ 390 g)",
        "poidsG": 390,
        "prix": 7.25,
        "prixKg": 18.59
      },
      {
        "libelle": "12 brochettes (≈ 780 g)",
        "poidsG": 780,
        "prix": 14.5,
        "prixKg": 18.59
      }
    ],
    "portionRule": "BROCHETTE",
    "portion": {
      "minG": 150,
      "maxG": 3000,
      "pasG": 50,
      "gParPersonne": 200,
      "modeDefaut": "PIECE",
      "poidsReferenceG": 390
    },
    "race": null,
    "raceOrigine": null,
    "bms": null,
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [],
    "halal": false,
    "origine": "Découpe à Sannois",
    "origineDetail": {
      "ne": null,
      "eleve": "France",
      "abattu": "France (notre abattoir, Pierrelaye)",
      "decoupe": "France (Sannois)"
    },
    "espece": "VOLAILLE",
    "especes": [
      "VOLAILLE"
    ],
    "description": "Filets de poulet marinés à l’huile d’olive et aux épices. Six ou douze brochettes, prêtes à griller.",
    "conseil": "Nos préparations ne contiennent aucun additif : cuisez-les à feu doux, elles rendent plus de gras et ne se dessèchent pas.",
    "conservation": null,
    "cuisson": [],
    "temperatures": null,
    "reposMin": null,
    "allergenes": [],
    "ingredients": "Filet de poulet, huile d'olive, épices, sel.",
    "composition": [],
    "options": [
      {
        "cle": "MARINADE",
        "libelle": "Marinade",
        "valeurs": [
          "Nature",
          "Curry",
          "Tex-mex"
        ]
      }
    ],
    "tags": [
      "barbecue",
      "fait_maison"
    ],
    "photos": [
      {
        "url": "https://images.unsplash.com/photo-1432139555190-58524dae6a55?auto=format&fit=crop&w=1200&q=72",
        "alt": "Brochettes grillées servies avec légumes",
        "legende": "Cuit — suggestion de service",
        "credit": "Unsplash · Mgg Vitchakorn"
      }
    ],
    "photo": "https://images.unsplash.com/photo-1432139555190-58524dae6a55?auto=format&fit=crop&w=1200&q=72",
    "vignette": "https://images.unsplash.com/photo-1432139555190-58524dae6a55?auto=format&fit=crop&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_agneau_gigot",
    "slug": "gigot-d-agneau",
    "nom": "Gigot d'agneau raccourci",
    "categorieId": "cat_agneau",
    "famille": "agneau",
    "familleNom": "Agneau",
    "modele": "AU_KG",
    "prixKg": 26.9,
    "prixFixe": null,
    "poidsG": null,
    "formats": [],
    "portionRule": "GIGOT_OS",
    "portion": {
      "minG": 800,
      "maxG": 3500,
      "pasG": 100,
      "gParPersonne": 325,
      "modeDefaut": "PERSONNES",
      "poidsReferenceG": null
    },
    "race": null,
    "raceOrigine": null,
    "bms": null,
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [
      "Agneau de France"
    ],
    "halal": false,
    "origine": "Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": null,
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "OVIN",
    "especes": [
      "OVIN"
    ],
    "description": "Gigot d’agneau raccourci, os conservé pour le goût. Rôti à 200 °C, rosé au centre, parfumé au thym.",
    "conseil": "Rosé au centre : quinze minutes par livre à 200 °C, puis dix minutes de repos sous une feuille d’aluminium.",
    "conservation": "3 jours au réfrigérateur entre 0 et 4 °C",
    "cuisson": [
      "Four",
      "Rôtissoire"
    ],
    "temperatures": {
      "rosé": 58,
      "aPoint": 63
    },
    "reposMin": 10,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [
      {
        "cle": "PREPARATION",
        "libelle": "Préparation",
        "valeurs": [
          "Avec os",
          "Désossé et ficelé",
          "Manchon"
        ]
      }
    ],
    "tags": [
      "dimanche"
    ],
    "photos": [
      {
        "url": "https://images.unsplash.com/photo-1580554530778-ca36943938b2?auto=format&fit=crop&w=1200&q=72",
        "alt": "Agneau tranché présenté sur assiette, cuisson rosée",
        "legende": "Cru — planche en bois",
        "credit": "Unsplash · Sebastian Coman Photography"
      },
      {
        "url": "https://commons.wikimedia.org/wiki/Special:FilePath/Gigot_d_agneau_2019.jpg?width=1200",
        "alt": "Gigot d'agneau rôti",
        "legende": "Cuit — suggestion de service",
        "credit": "Wikimedia Commons · Cloudapple"
      }
    ],
    "photo": "https://images.unsplash.com/photo-1580554530778-ca36943938b2?auto=format&fit=crop&w=1200&q=72",
    "vignette": "https://images.unsplash.com/photo-1580554530778-ca36943938b2?auto=format&fit=crop&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_entrecote_maturee",
    "slug": "entrecote-maturee-28-jours",
    "nom": "Entrecôte maturée 28 jours",
    "categorieId": "cat_boeuf",
    "famille": "boeuf",
    "familleNom": "Bœuf & Génisse",
    "modele": "AU_KG",
    "prixKg": 46.9,
    "prixFixe": null,
    "poidsG": null,
    "formats": [],
    "portionRule": "A_GRILLER",
    "portion": {
      "minG": 150,
      "maxG": 1500,
      "pasG": 50,
      "gParPersonne": 200,
      "modeDefaut": "PERSONNES",
      "poidsReferenceG": null
    },
    "race": "Charolaise",
    "raceOrigine": "France",
    "bms": "6",
    "maturationJours": 28,
    "maturationMethode": "Sur os, cabinet maison",
    "labels": [
      "Viande Bovine Française"
    ],
    "halal": false,
    "origine": "Né et élevé en France · Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": "France",
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "BOVIN",
    "especes": [
      "BOVIN"
    ],
    "description": "Entrecôte maturée vingt-huit jours, persillée à souhait. La pièce à saisir à la plancha, tranchée en travers.",
    "conseil": "Sortez la pièce du froid trente minutes avant de la saisir, salez au dernier moment, et laissez-la reposer après cuisson : c’est là que le jus se réinstalle.",
    "conservation": null,
    "cuisson": [
      "Braise",
      "Plancha"
    ],
    "temperatures": {
      "bleu": 48,
      "saignant": 54,
      "aPoint": 60
    },
    "reposMin": 5,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [
      {
        "cle": "EPAISSEUR",
        "libelle": "Épaisseur de coupe",
        "valeurs": [
          "2 cm",
          "3 cm",
          "4 cm"
        ]
      }
    ],
    "tags": [
      "grillade",
      "best_seller"
    ],
    "photos": [
      {
        "url": "https://images.unsplash.com/photo-1615937691194-97dbd3f3dc29?auto=format&fit=crop&w=1200&q=72",
        "alt": "Pièces de bœuf crues sur papier de boucherie",
        "legende": "Cru — fond blanc pur",
        "credit": "Unsplash · Nick Karvounis"
      },
      {
        "url": "https://images.unsplash.com/photo-1560781290-7dc94c0f8f4f?auto=format&fit=crop&w=1200&q=72",
        "alt": "Pièce de bœuf crue sur planche de bois avec romarin",
        "legende": "Cru — planche en bois",
        "credit": "Unsplash · Jason Leung"
      },
      {
        "url": "https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?auto=format&fit=crop&w=1200&q=72",
        "alt": "Viande tranchée, cuisson rosée",
        "legende": "Cuit — suggestion de service",
        "credit": "Unsplash · Amirali Mirhashemian"
      }
    ],
    "photo": "https://images.unsplash.com/photo-1615937691194-97dbd3f3dc29?auto=format&fit=crop&w=1200&q=72",
    "vignette": "https://images.unsplash.com/photo-1615937691194-97dbd3f3dc29?auto=format&fit=crop&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_chipolata",
    "slug": "chipolata-maison",
    "nom": "Chipolata maison",
    "categorieId": "cat_preparations",
    "famille": "preparations-maison",
    "familleNom": "Préparations Maison",
    "modele": "FORMATS",
    "prixKg": 14.99,
    "prixFixe": null,
    "poidsG": null,
    "formats": [
      {
        "libelle": "500 g",
        "poidsG": 500,
        "prix": 7.5,
        "prixKg": 15.0
      },
      {
        "libelle": "1 kg",
        "poidsG": 1000,
        "prix": 14.99,
        "prixKg": 14.99
      },
      {
        "libelle": "2 kg",
        "poidsG": 2000,
        "prix": 28,
        "prixKg": 14.0
      }
    ],
    "portionRule": "HACHE_SAUCISSE",
    "portion": {
      "minG": 250,
      "maxG": 3000,
      "pasG": 50,
      "gParPersonne": 175,
      "modeDefaut": "POIDS",
      "poidsReferenceG": 500
    },
    "race": null,
    "raceOrigine": null,
    "bms": null,
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [
      "Fait maison"
    ],
    "halal": false,
    "origine": "Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": null,
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "CANARD",
    "especes": [
      "CANARD"
    ],
    "description": "Chipolata au canard fermier, aux herbes fraîches, boyau naturel. Douze pièces, à griller ou à poêler.",
    "conseil": "Nos préparations ne contiennent aucun additif : cuisez-les à feu doux, elles rendent plus de gras et ne se dessèchent pas.",
    "conservation": null,
    "cuisson": [],
    "temperatures": null,
    "reposMin": null,
    "allergenes": [
      "sulfites"
    ],
    "ingredients": "Viande de canard, sel, épices, boyau naturel de canard.",
    "composition": [],
    "options": [],
    "tags": [
      "barbecue",
      "fait_maison"
    ],
    "photos": [
      {
        "url": "https://commons.wikimedia.org/wiki/Special:FilePath/Saucisse_de_Toulouse.jpg?width=1200",
        "alt": "Chipolatas crues sur une assiette",
        "legende": "Cru — fond blanc pur",
        "credit": "Wikimedia Commons · Slastic"
      },
      {
        "url": "https://commons.wikimedia.org/wiki/Special:FilePath/Barbecue%2C_merguez%2C_saucisses_et_brochettes.JPG?width=1200",
        "alt": "Saucisses grillées au barbecue",
        "legende": "Cuit — suggestion de service",
        "credit": "Wikimedia Commons · Benoît Prieur"
      }
    ],
    "photo": "https://commons.wikimedia.org/wiki/Special:FilePath/Saucisse_de_Toulouse.jpg?width=1200",
    "vignette": "https://commons.wikimedia.org/wiki/Special:FilePath/Saucisse_de_Toulouse.jpg?width=1200&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois"
    ],
    "indisponibleEn": [
      "store_franconville",
      "store_argenteuil"
    ]
  },
  {
    "id": "p_boudin_noir",
    "slug": "boudin-noir-maison",
    "nom": "Boudin noir maison",
    "categorieId": "cat_triperie",
    "famille": "triperie-abats",
    "familleNom": "Triperie / Abats",
    "modele": "AU_KG",
    "prixKg": 16.5,
    "prixFixe": null,
    "poidsG": null,
    "formats": [],
    "portionRule": "HACHE_SAUCISSE",
    "portion": {
      "minG": 250,
      "maxG": 3000,
      "pasG": 50,
      "gParPersonne": 175,
      "modeDefaut": "POIDS",
      "poidsReferenceG": null
    },
    "race": null,
    "raceOrigine": null,
    "bms": null,
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [
      "Fait maison"
    ],
    "halal": false,
    "origine": "Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": null,
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "CANARD",
    "especes": [
      "CANARD"
    ],
    "description": "Sang frais, oignons fondus, pommes. Le boudin de la maison, à cuire à la poêle sans jamais le piquer.",
    "conseil": "Le boudin se réchauffe à feu doux, jamais à gros bouillons, et ne se pique pas : c’est la peau qui le tient.",
    "conservation": "3 jours au réfrigérateur entre 0 et 4 °C",
    "cuisson": [
      "Poêle",
      "Four"
    ],
    "temperatures": {
      "aPoint": 71
    },
    "reposMin": null,
    "allergenes": [],
    "ingredients": "Sang de canard, oignons, pommes, épices, boyau naturel de canard.",
    "composition": [],
    "options": [],
    "tags": [
      "fait_maison"
    ],
    "photos": [
      {
        "url": "https://commons.wikimedia.org/wiki/Special:FilePath/Butifarra_negra.jpg?width=1200",
        "alt": "Boudin noir entier, une extrémité tranchée",
        "legende": "Cru — fond blanc pur",
        "credit": "Wikimedia Commons · Slastic"
      }
    ],
    "photo": "https://commons.wikimedia.org/wiki/Special:FilePath/Butifarra_negra.jpg?width=1200",
    "vignette": "https://commons.wikimedia.org/wiki/Special:FilePath/Butifarra_negra.jpg?width=1200&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois"
    ],
    "indisponibleEn": [
      "store_franconville",
      "store_argenteuil"
    ]
  },
  {
    "id": "p_cote_canard",
    "slug": "cote-de-canard-ferme",
    "nom": "Côte de canard fermier",
    "categorieId": "cat_canard",
    "famille": "canard-charcuterie",
    "familleNom": "Canard & Charcuterie",
    "modele": "AU_KG",
    "prixKg": 13.9,
    "prixFixe": null,
    "poidsG": null,
    "formats": [],
    "portionRule": "A_GRILLER",
    "portion": {
      "minG": 150,
      "maxG": 1500,
      "pasG": 50,
      "gParPersonne": 200,
      "modeDefaut": "PERSONNES",
      "poidsReferenceG": null
    },
    "race": "Canard Noir de Bigorre",
    "raceOrigine": "France",
    "bms": "7",
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [
      "Canard de France"
    ],
    "halal": false,
    "origine": "Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": null,
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "CANARD",
    "especes": [
      "CANARD"
    ],
    "description": "Côte de canard fermier avec os, cuisson douce, rosée au centre. Jus de pomme et sauge s’il vous reste une branche.",
    "conseil": "La côte de canard reste rosée : arrêtez la cuisson quand le jus perle en surface. Une viande fermière supporte d’être servie saignante.",
    "conservation": "3 jours au réfrigérateur entre 0 et 4 °C",
    "cuisson": [
      "Braise",
      "Plancha",
      "Four"
    ],
    "temperatures": {
      "aPoint": 63
    },
    "reposMin": 3,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [
      {
        "cle": "EPAISSEUR",
        "libelle": "Épaisseur de coupe",
        "valeurs": [
          "1,5 cm",
          "2 cm"
        ]
      }
    ],
    "tags": [
      "grillade"
    ],
    "photos": [
      {
        "url": "https://images.unsplash.com/photo-1588347818036-558601350947?auto=format&fit=crop&w=1200&q=72",
        "alt": "Côte de canard servie avec pommes de terre et brocoli",
        "legende": "Cuit — suggestion de service",
        "credit": "Unsplash · Andrew Seaman"
      }
    ],
    "photo": "https://images.unsplash.com/photo-1588347818036-558601350947?auto=format&fit=crop&w=1200&q=72",
    "vignette": "https://images.unsplash.com/photo-1588347818036-558601350947?auto=format&fit=crop&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois"
    ],
    "indisponibleEn": [
      "store_franconville",
      "store_argenteuil"
    ]
  },
  {
    "id": "p_poulet_mechoui_pilon",
    "slug": "pilon-de-poulet-mechoui",
    "nom": "Pilon de poulet méchoui",
    "categorieId": "cat_mechoui",
    "famille": "mechoui-receptions",
    "familleNom": "Méchoui & Réceptions",
    "modele": "AU_KG",
    "prixKg": 9.99,
    "prixFixe": null,
    "poidsG": null,
    "formats": [],
    "portionRule": "HACHE_SAUCISSE",
    "portion": {
      "minG": 250,
      "maxG": 3000,
      "pasG": 50,
      "gParPersonne": 175,
      "modeDefaut": "POIDS",
      "poidsReferenceG": null
    },
    "race": null,
    "raceOrigine": null,
    "bms": null,
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [
      "Halal"
    ],
    "halal": true,
    "origine": "Découpe à Sannois",
    "origineDetail": {
      "ne": null,
      "eleve": "France",
      "abattu": "France (notre abattoir, Sannois)",
      "decoupe": "France (Sannois)"
    },
    "espece": "VOLAILLE",
    "especes": [
      "VOLAILLE"
    ],
    "description": "Pilons marinés pour la broche, cuisson lente. Parfaits pour un buffet de quinze personnes.",
    "conseil": "Commandez soixante-douze heures à l’avance. Nous préparons, marinons et mettons en broche le jour même ; vous n’avez qu’à surveiller le feu.",
    "conservation": null,
    "cuisson": [
      "Four",
      "Rôtissoire",
      "Braise"
    ],
    "temperatures": {
      "aPoint": 74
    },
    "reposMin": null,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [],
    "tags": [
      "evenement",
      "mechoui",
      "barbecue"
    ],
    "photos": [
      {
        "url": "https://images.unsplash.com/photo-1432139555190-58524dae6a55?auto=format&fit=crop&w=1200&q=72",
        "alt": "Pièces grillées à la broche, service au plat",
        "legende": "Sur broche, cuit",
        "credit": "Unsplash · Mgg Vitchakorn"
      }
    ],
    "photo": "https://images.unsplash.com/photo-1432139555190-58524dae6a55?auto=format&fit=crop&w=1200&q=72",
    "vignette": "https://images.unsplash.com/photo-1432139555190-58524dae6a55?auto=format&fit=crop&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  },
  {
    "id": "p_colis_barbecue_8",
    "slug": "colis-barbecue-8-personnes",
    "nom": "Colis Barbecue 8 personnes",
    "categorieId": "cat_colis",
    "famille": "colis-coffrets",
    "familleNom": "Colis & Coffrets",
    "modele": "COLIS",
    "prixKg": 37.46,
    "prixFixe": 89.9,
    "poidsG": 2400,
    "formats": [],
    "portionRule": "COLIS_8",
    "portion": {
      "minG": 1600,
      "maxG": 3200,
      "pasG": 100,
      "gParPersonne": 300,
      "modeDefaut": "PIECE",
      "poidsReferenceG": 2400
    },
    "race": null,
    "raceOrigine": null,
    "bms": null,
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [],
    "halal": false,
    "origine": "Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": null,
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "MULTI",
    "especes": [
      "BOVIN",
      "OVIN",
      "CANARD",
      "VOLAILLE"
    ],
    "description": "Quatre viandes, deux kilos quatre, pour huit convives. Le panier prêt à griller, sans réfléchir aux quantités.",
    "conseil": "Le colis arrive prêt : laissez-le trente minutes à température ambiante avant de griller, les fibres se détendent.",
    "conservation": null,
    "cuisson": [],
    "temperatures": null,
    "reposMin": null,
    "allergenes": [],
    "ingredients": null,
    "composition": [
      {
        "name": "Merguez maison",
        "weightG": 800
      },
      {
        "name": "Brochettes de poulet",
        "weightG": 780
      },
      {
        "name": "Côte de canard fermier",
        "weightG": 600
      },
      {
        "name": "Chipolata maison",
        "weightG": 400
      }
    ],
    "options": [],
    "tags": [
      "colis",
      "barbecue"
    ],
    "photos": [
      {
        "url": "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=1200&q=72",
        "alt": "Assortiment de viandes crues pour un barbecue",
        "legende": "Cru — fond blanc pur",
        "credit": "Unsplash · Jason Leung"
      },
      {
        "url": "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=1200&q=72",
        "alt": "Travers laqués servis avec frites et pickles",
        "legende": "Cuit — suggestion de service",
        "credit": "Unsplash · Andrey Zvyagintsev"
      }
    ],
    "photo": "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=1200&q=72",
    "vignette": "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois"
    ],
    "indisponibleEn": [
      "store_franconville",
      "store_argenteuil"
    ]
  },
  {
    "id": "p_pot_au_feu",
    "slug": "pot-au-feu-complet",
    "nom": "Pot-au-feu complet (viande + os à moelle)",
    "categorieId": "cat_boeuf",
    "famille": "boeuf",
    "familleNom": "Bœuf & Génisse",
    "modele": "AU_KG",
    "prixKg": 17.9,
    "prixFixe": null,
    "poidsG": null,
    "formats": [],
    "portionRule": "A_MIJOTER",
    "portion": {
      "minG": 500,
      "maxG": 5000,
      "pasG": 100,
      "gParPersonne": 275,
      "modeDefaut": "PERSONNES",
      "poidsReferenceG": null
    },
    "race": "Charolaise",
    "raceOrigine": "France",
    "bms": "5–6",
    "maturationJours": null,
    "maturationMethode": null,
    "labels": [
      "Viande Bovine Française"
    ],
    "halal": false,
    "origine": "Né et élevé en France · Abattu en France · Découpe à Sannois",
    "origineDetail": {
      "ne": "France",
      "eleve": "France",
      "abattu": "France",
      "decoupe": "France (Sannois)"
    },
    "espece": "BOVIN",
    "especes": [
      "BOVIN"
    ],
    "description": "Joue, poitrine, paleron et os à moelle dans le même panier. Le bouillon du dimanche, complet, à faire doucement.",
    "conseil": "Sortez la pièce du froid trente minutes avant de la saisir, salez au dernier moment, et laissez-la reposer après cuisson : c’est là que le jus se réinstalle.",
    "conservation": null,
    "cuisson": [
      "Cocotte"
    ],
    "temperatures": {
      "aPoint": 85
    },
    "reposMin": null,
    "allergenes": [],
    "ingredients": null,
    "composition": [],
    "options": [
      {
        "cle": "PREPARATION",
        "libelle": "Préparation",
        "valeurs": [
          "En morceaux 5 cm",
          "En morceaux 8 cm"
        ]
      }
    ],
    "tags": [
      "a_mijoter",
      "hiver",
      "recette"
    ],
    "photos": [
      {
        "url": "https://commons.wikimedia.org/wiki/Special:FilePath/Pot-au-feu_SAM_2723.JPG?width=1200",
        "alt": "Pot-au-feu servi : viande, légumes et bouillon",
        "legende": "Cuit — suggestion de service",
        "credit": "Wikimedia Commons · Muesse"
      }
    ],
    "photo": "https://commons.wikimedia.org/wiki/Special:FilePath/Pot-au-feu_SAM_2723.JPG?width=1200",
    "vignette": "https://commons.wikimedia.org/wiki/Special:FilePath/Pot-au-feu_SAM_2723.JPG?width=1200&w=600",
    "abattageMaison": true,
    "disponibleEn": [
      "store_sannois",
      "store_franconville",
      "store_argenteuil"
    ],
    "indisponibleEn": []
  }
];

export const AVIS: Avis[] = [
  {
    "id": "av1",
    "auteur": "Claire M.",
    "ville": "Sannois",
    "note": 5,
    "date": "2026-10-12",
    "produit": "cote-de-boeuf-maturee-28-jours",
    "titre": "La côte du dimanche, sans se tromper",
    "texte": "Commandée le jeudi pour le samedi matin. Le boucher avait noté mes préférences d’épaisseur, tout était prêt à l’heure. La maturation se sent vraiment : un goût de noisette, et une cuisson qui pardonne."
  },
  {
    "id": "av2",
    "auteur": "Julien R.",
    "ville": "Franconville",
    "note": 5,
    "date": "2026-10-04",
    "produit": "agneau-entier-mechoui",
    "titre": "Le méchoui qui a sauvé le baptême",
    "texte": "Trente-deux personnes, un agneau entier. Livré mariné et en broche, avec la fiche de cuisson. Le boucher nous a même rappelés la veille pour le timing. On a eu des félicitations jusqu’au dessert."
  },
  {
    "id": "av3",
    "auteur": "Nadia B.",
    "ville": "Argenteuil",
    "note": 4,
    "date": "2026-09-28",
    "produit": "merguez-maison",
    "titre": "Enfin une merguez qui a du goût",
    "texte": "Pas d’additif, ça se sent tout de suite à la cuisson. Un peu plus grasse que celle du supermarché, mais c’est justement ce qu’on cherche. Le format un kilo est parfait pour la semaine."
  },
  {
    "id": "av4",
    "auteur": "Michel D.",
    "ville": "Sannois",
    "note": 5,
    "date": "2026-09-20",
    "produit": "roti-de-veau",
    "titre": "Le veau comme chez ma grand-mère",
    "texte": "Le rôti ficelé main, la cuisson à 160 °C, le jus clair. C’est exactement ce que je cherchais depuis vingt ans, et je n’ai plus besoin de traverser le département."
  },
  {
    "id": "av5",
    "auteur": "Sophie L.",
    "ville": "Ermont",
    "note": 5,
    "date": "2026-09-14",
    "produit": "colis-barbecue-8-personnes",
    "titre": "Le colis qui évite de réfléchir",
    "texte": "Huit personnes, quatre viandes, tout était prévu. On a juste ajouté du pain. Le prix au kilo reste correct pour de la viande de boucherie, et le colis isotherme a tenu jusqu’au soir."
  },
  {
    "id": "av6",
    "auteur": "Karim T.",
    "ville": "Franconville",
    "note": 5,
    "date": "2026-09-02",
    "produit": "agneau-gigot",
    "titre": "Retrait en dix minutes",
    "texte": "Commandé en ligne le matin, retiré l’après-midi au comptoir de Franconville. Le gigot était paré comme demandé et l’étiquette indiquait le poids réel, sans surprise."
  },
  {
    "id": "av7",
    "auteur": "Isabelle P.",
    "ville": "Cormeilles-en-Parisis",
    "note": 4,
    "date": "2026-08-22",
    "produit": "poulet-fermier-entier",
    "titre": "Une volaille qui a du goût",
    "texte": "La différence avec un poulet de grande surface est nette : la chair est ferme, le bouillon excellent. Une étoile en moins pour le délai d’un jour sur la livraison, réglé par un appel aimable."
  },
  {
    "id": "av8",
    "auteur": "Antoine G.",
    "ville": "Sannois",
    "note": 5,
    "date": "2026-08-09",
    "produit": "pot-au-feu-complet",
    "titre": "Le bouillon du dimanche, complet",
    "texte": "Tout y est : la joue, la poitrine, l’os à moelle. J’ai suivi la fiche de cuisson, trois heures à petit frémissement. Ma fille de quatorze ans en a repris deux fois."
  }
];

export const QUESTIONS: Question[] = [
  {
    "question": "Pourquoi le prix affiché est-il une estimation ?",
    "reponse": "Une pièce de viande ne pèse jamais exactement le poids demandé. Vous choisissez un poids, nous découpons au plus juste, puis nous pesons. Le prix final est calculé sur le poids réel, dans une tolérance de ±5 % : au-delà, la Maison absorbe l’écart, jamais vous."
  },
  {
    "question": "Qu’est-ce que les arrhes, et quand sont-elles demandées ?",
    "reponse": "Les arrhes réservent votre pièce : la maison l’achète et la prépare pour vous. Elles ne sont pas demandées en dessous de 50 €. Entre 50 et 120 €, elles représentent 20 %, avec la possibilité de tout régler au comptoir. Au-delà de 120 €, ou pour une pièce préparée sur mesure, elles sont renforcées. La livraison, elle, se règle intégralement en ligne."
  },
  {
    "question": "Combien de temps à l’avance faut-il commander ?",
    "reponse": "Deux heures pour une pièce standard, vingt-quatre heures pour une découpe particulière (épaisseur, parage), quarante-huit heures pour une pièce maturée, soixante-douze heures pour un agneau entier de méchoui. Les créneaux proposés au moment de la commande tiennent compte de ces délais."
  },
  {
    "question": "Puis-je changer mon créneau après la commande ?",
    "reponse": "Oui, tant que la pièce n’est pas découpée. Un lien dans l’e-mail de confirmation permet de choisir un autre créneau ; l’atelier est prévenu automatiquement et votre place est rendue aux autres clients."
  },
  {
    "question": "Comment se passe la livraison réfrigérée ?",
    "reponse": "Colis isotherme maintenu entre 0 et 4 °C, expédié du mardi au samedi. Livraison offerte dès 99 € d’achat. En cas de rupture de la chaîne du froid à la réception, vous avez 4 heures pour nous le signaler : nous remplaçons ou nous remboursons, sans discussion."
  },
  {
    "question": "Vos viandes sont-elles halal ?",
    "reponse": "Notre agneau et la volaille des boutiques de Franconville et Argenteuil sont halal, identifiés par le label sur la fiche produit. Le canard et le sanglier sont exclus de ces deux boutiques ; ils restent vendus à Sannois, où la charcuterie maison est travaillée."
  }
];

export const ETAPES: Etape[] = [
  {
    "cle": "poids",
    "titre": "Vous choisissez un poids",
    "texte": "Par personne, à la pièce ou dans un format. À la pièce, nous visons le poids juste — la fourchette est annoncée avant le panier."
  },
  {
    "cle": "prix",
    "titre": "Le prix est une estimation",
    "texte": "Prix au kilo affiché, estimation recalculée sur le poids choisi. À la pesée, l'écart reste dans ±5 % : au-delà, la maison l'absorbe."
  },
  {
    "cle": "heure",
    "titre": "Le créneau est tenu",
    "texte": "Découpe sous 24 h, maturation 48 h, agneau entier 72 h. Le créneau proposé tient compte de ce délai."
  },
  {
    "cle": "retrait",
    "titre": "Vous payez le juste poids",
    "texte": "Au retrait ou à la livraison, la différence de pesée est réglée en une fois : le solde est calculé sur le poids réel, jamais sur l'estimation."
  }
];

export const VALEURS: Valeur[] = [
  {
    "titre": "On ne vend que ce qu'on a choisi",
    "texte": "Trois élevages, pas trente. Des bêtes vues sur pied, un abattage que nous contrôlons, et une découpe faite ici."
  },
  {
    "titre": "Le prix se dit avant la commande",
    "texte": "Le prix au kilo et la tolérance de pesée sont affichés sur chaque fiche. Aucun supplément n'apparaît au moment de payer."
  },
  {
    "titre": "Une pièce se retire à l'heure dite",
    "texte": "Les créneaux sont dimensionnés sur la capacité de l'atelier, jamais sur l'envie de remplir le carnet. Si nous ne tenons pas, nous prévenons."
  }
];

export const HISTOIRE: Jalon[] = [
  {
    "annee": "1922",
    "titre": "Un étal, rue d'Argenteuil",
    "texte": "Marcel Duval installe son billot à Sannois. Trois bêtes par semaine, une clientèle de quartier, et la même règle : montrer la pièce avant de la vendre."
  },
  {
    "annee": "1968",
    "titre": "L'atelier d'abattage",
    "texte": "Michel Duval reprend l'affaire et fait construire l'atelier de Sannois. On ne dépend plus d'un intermédiaire pour savoir d'où vient la bête."
  },
  {
    "annee": "1994",
    "titre": "Le cabinet de maturation",
    "texte": "Premier cabinet sur os du Val-d'Oise. Vingt-huit jours deviennent la norme maison ; quarante-cinq pour les pièces d'exception."
  },
  {
    "annee": "2011",
    "titre": "Franconville, puis Argenteuil",
    "texte": "Deux nouvelles boutiques, un rayon halal complet, et la charcuterie maison travaillée deux fois par semaine."
  },
  {
    "annee": "2026",
    "titre": "La commande en ligne, sans trahir le comptoir",
    "texte": "Poids, prix, créneau : les trois informations du comptoir deviennent trois étapes lisibles. Le boucher reste joignable, et le conseil ne se perd pas dans un formulaire."
  }
];

export const COMMANDES: Commande[] = [
  {
    "numero": "CMD-2026-1048",
    "date": "17/10/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "EN_PREPARATION",
    "statutLisible": "En préparation",
    "creneau": "sam. 17 oct. · 10h00–10h30",
    "boutiqueId": "store_sannois",
    "clientNom": "Nadia B.",
    "clientTelephone": "06 12 34 56 78",
    "total": 59.88,
    "arrhes": 12,
    "solde": 47.88,
    "lignes": [
      {
        "nom": "Côte de bœuf maturée 28 jours",
        "quantite": 1,
        "prix": 59.88,
        "options": "épaisseur 3 cm · ficelée",
        "poidsEstimeG": 1200,
        "poidsReelG": null,
        "ecartPct": null,
        "prixKg": 49.9,
        "prixFinal": null,
        "pese": false
      }
    ],
    "paye": 12,
    "totalFinal": null,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1041",
    "date": "17/10/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "POIDS_SAISI",
    "statutLisible": "POIDS_SAISI",
    "creneau": "sam. 17 oct. · 09h30–10h00",
    "boutiqueId": "store_sannois",
    "clientNom": "Jean-Pierre M.",
    "clientTelephone": "06 45 78 12 30",
    "total": 105.91,
    "arrhes": 0,
    "solde": 107.42,
    "lignes": [
      {
        "nom": "Rôti de veau",
        "quantite": 1,
        "prix": 61.19,
        "options": "ficelé",
        "poidsEstimeG": 1800,
        "poidsReelG": 1860,
        "ecartPct": 3.3,
        "prixKg": 32.9,
        "prixFinal": 61.19,
        "pese": true
      },
      {
        "nom": "Poulet fermier entier",
        "quantite": 1,
        "prix": 22.61,
        "options": "prêt à rôtir",
        "poidsEstimeG": 1600,
        "poidsReelG": 1580,
        "ecartPct": -1.3,
        "prixKg": 14.31,
        "prixFinal": 22.61,
        "pese": true
      },
      {
        "nom": "Merguez maison douce",
        "quantite": 1,
        "prix": 19.13,
        "options": "boyau naturel",
        "poidsEstimeG": 1000,
        "poidsReelG": 1020,
        "ecartPct": 2,
        "prixKg": 18.75,
        "prixFinal": 19.13,
        "pese": true
      },
      {
        "nom": "Brochettes de poulet curry",
        "quantite": 1,
        "prix": 14.5,
        "options": "12 brochettes",
        "poidsEstimeG": 780,
        "poidsReelG": 780,
        "ecartPct": 0,
        "prixKg": 18.59,
        "prixFinal": 14.5,
        "pese": true
      }
    ],
    "paye": 0,
    "totalFinal": 107.42,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1052",
    "date": "20/10/2026",
    "mode": "DELIVERY",
    "modeLisible": "Livraison réfrigérée",
    "statut": "EXPEDIEE",
    "statutLisible": "Expédiée",
    "creneau": "mar. 20 oct. · 9h–13h (Chronofresh)",
    "boutiqueId": "store_sannois",
    "clientNom": "Élodie & Marc",
    "clientTelephone": "07 88 22 44 66",
    "total": 194.88,
    "arrhes": 0,
    "solde": 0,
    "lignes": [
      {
        "nom": "Entrecôte Wagyu croisé",
        "quantite": 1,
        "prix": 135.0,
        "options": "2 pièces · 3 cm",
        "poidsEstimeG": 900,
        "poidsReelG": null,
        "ecartPct": null,
        "prixKg": 150,
        "prixFinal": null,
        "pese": false
      },
      {
        "nom": "Côte de bœuf maturée 28 jours",
        "quantite": 1,
        "prix": 59.88,
        "options": "épaisseur 4 cm",
        "poidsEstimeG": 1200,
        "poidsReelG": null,
        "ecartPct": null,
        "prixKg": 49.9,
        "prixFinal": null,
        "pese": false
      }
    ],
    "paye": 0,
    "totalFinal": null,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "20/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "20/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": true,
        "date": "20/10/2026"
      },
      {
        "titre": "Prête au retrait",
        "fait": true,
        "date": "20/10/2026"
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1055",
    "date": "17/10/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "NOUVELLE",
    "statutLisible": "NOUVELLE",
    "creneau": "sam. 17 oct. · 11h00–11h30",
    "boutiqueId": "store_sannois",
    "clientNom": "Jean-Pierre M.",
    "clientTelephone": "06 45 78 12 30",
    "total": 42.9,
    "arrhes": 0,
    "solde": 42.9,
    "lignes": [
      {
        "nom": "Gigot d'agneau raccourci",
        "quantite": 1,
        "prix": 42.91,
        "options": "avec os",
        "poidsEstimeG": 1595,
        "poidsReelG": null,
        "ecartPct": null,
        "prixKg": 26.9,
        "prixFinal": null,
        "pese": false
      }
    ],
    "paye": 0,
    "totalFinal": null,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1056",
    "date": "24/10/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "NOUVELLE",
    "statutLisible": "NOUVELLE",
    "creneau": "sam. 24 oct. · 09h00–09h30",
    "boutiqueId": "store_sannois",
    "clientNom": "Karim A.",
    "clientTelephone": "06 71 09 55 12",
    "total": 302.98,
    "arrhes": 144.5,
    "solde": 158.48,
    "lignes": [
      {
        "nom": "Agneau entier méchoui",
        "quantite": 1,
        "prix": 289,
        "options": "découpé en 8 morceaux · 18 kg",
        "poidsEstimeG": 18000,
        "poidsReelG": null,
        "ecartPct": null,
        "prixKg": 16.06,
        "prixFinal": 289,
        "pese": false
      },
      {
        "nom": "Pommes de terre méchoui",
        "quantite": 1,
        "prix": 13.98,
        "options": "2 × 2,5 kg",
        "poidsEstimeG": null,
        "poidsReelG": null,
        "ecartPct": null,
        "prixKg": null,
        "prixFinal": 13.98,
        "pese": false
      }
    ],
    "paye": 144.5,
    "totalFinal": null,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "24/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "24/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1036",
    "date": "17/10/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "PRETE",
    "statutLisible": "Prête au retrait",
    "creneau": "sam. 17 oct. · 09h00–09h30",
    "boutiqueId": "store_sannois",
    "clientNom": "Sophie L.",
    "clientTelephone": "",
    "total": 63.1,
    "arrhes": 13,
    "solde": 48.88,
    "lignes": [
      {
        "nom": "Entrecôte Black Angus",
        "quantite": 1,
        "prix": 24.59,
        "options": "3 cm · 2 pièces",
        "poidsEstimeG": 460,
        "poidsReelG": 452,
        "ecartPct": -1.7,
        "prixKg": 54.4,
        "prixFinal": 24.59,
        "pese": true
      },
      {
        "nom": "Merguez maison piquante",
        "quantite": 1,
        "prix": 19.13,
        "options": "1 kg",
        "poidsEstimeG": 1000,
        "poidsReelG": 1020,
        "ecartPct": 2,
        "prixKg": 18.75,
        "prixFinal": 19.13,
        "pese": true
      },
      {
        "nom": "Pommes de terre méchoui",
        "quantite": 1,
        "prix": 6.99,
        "options": "1 × 2,5 kg",
        "poidsEstimeG": null,
        "poidsReelG": null,
        "ecartPct": null,
        "prixKg": null,
        "prixFinal": 6.99,
        "pese": false
      }
    ],
    "paye": 13,
    "totalFinal": 61.88,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "Prête au retrait",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1058",
    "date": "17/10/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "PAIEMENT_ECHEC",
    "statutLisible": "PAIEMENT_ECHEC",
    "creneau": "sam. 17 oct. · 15h00–15h30",
    "boutiqueId": "store_sannois",
    "clientNom": "Thomas R.",
    "clientTelephone": "06 33 77 88 99",
    "total": 121.97,
    "arrhes": 40,
    "solde": 121.97,
    "lignes": [
      {
        "nom": "Côte de bœuf maturée 28 jours",
        "quantite": 1,
        "prix": 69.86,
        "options": "Découpe : 2 côtes épaisses · os conservé",
        "poidsEstimeG": 1400,
        "poidsReelG": null,
        "ecartPct": null,
        "prixKg": 49.9,
        "prixFinal": null,
        "pese": false
      },
      {
        "nom": "Entrecôte maturée 28 jours",
        "quantite": 1,
        "prix": 42.21,
        "options": "Parées, sans gras de couverture",
        "poidsEstimeG": 900,
        "poidsReelG": null,
        "ecartPct": null,
        "prixKg": 46.9,
        "prixFinal": null,
        "pese": false
      },
      {
        "nom": "Boudin noir maison",
        "quantite": 1,
        "prix": 9.9,
        "options": "Boudin au poivre · à cuire au four",
        "poidsEstimeG": 600,
        "poidsReelG": null,
        "ecartPct": null,
        "prixKg": 16.5,
        "prixFinal": null,
        "pese": false
      }
    ],
    "paye": 0,
    "totalFinal": null,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "17/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1030",
    "date": "16/10/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "CLOTUREE",
    "statutLisible": "CLOTUREE",
    "creneau": "ven. 16 oct. · 10h30–11h00",
    "boutiqueId": "store_sannois",
    "clientNom": "Jean-Pierre M.",
    "clientTelephone": "06 45 78 12 30",
    "total": 61.49,
    "arrhes": 0,
    "solde": 0,
    "lignes": [
      {
        "nom": "Poulet fermier entier",
        "quantite": 1,
        "prix": 25.47,
        "options": "Vidé, prêt à cuire · ficelle de bridage",
        "poidsEstimeG": 1800,
        "poidsReelG": 1780,
        "ecartPct": -1.111,
        "prixKg": 14.31,
        "prixFinal": 25.47,
        "pese": true
      },
      {
        "nom": "Côte de canard fermier",
        "quantite": 1,
        "prix": 22.38,
        "options": "6 tranches épaisses",
        "poidsEstimeG": 1600,
        "poidsReelG": 1610,
        "ecartPct": 0.625,
        "prixKg": 13.9,
        "prixFinal": 22.38,
        "pese": true
      },
      {
        "nom": "Chipolata maison",
        "quantite": 1,
        "prix": 13.12,
        "options": "Nature (pas d’herbes)",
        "poidsEstimeG": 900,
        "poidsReelG": 875,
        "ecartPct": -2.778,
        "prixKg": 14.99,
        "prixFinal": 13.12,
        "pese": true
      }
    ],
    "paye": 60.97,
    "totalFinal": 60.97,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "16/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "16/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1019",
    "date": "12/10/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "NON_RETIREE",
    "statutLisible": "NON_RETIREE",
    "creneau": "lun. 12 oct. · 18h00–18h30",
    "boutiqueId": "store_sannois",
    "clientNom": "Nadia B.",
    "clientTelephone": "06 12 34 56 78",
    "total": 55.93,
    "arrhes": 12.0,
    "solde": 0,
    "lignes": [
      {
        "nom": "Rôti de veau",
        "quantite": 1,
        "prix": 57.25,
        "options": "ficelé",
        "poidsEstimeG": 1700,
        "poidsReelG": 1740,
        "ecartPct": 2.4,
        "prixKg": 32.9,
        "prixFinal": 57.25,
        "pese": true
      }
    ],
    "paye": 12.0,
    "totalFinal": 57.25,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "12/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "12/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1002",
    "date": "08/10/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "NON_RETIREE",
    "statutLisible": "NON_RETIREE",
    "creneau": "jeu. 8 oct. · 18h00–18h30",
    "boutiqueId": "store_sannois",
    "clientNom": "Karim A.",
    "clientTelephone": "06 71 09 55 12",
    "total": 52.42,
    "arrhes": 11.0,
    "solde": 0,
    "lignes": [
      {
        "nom": "Merguez maison",
        "quantite": 1,
        "prix": 18.38,
        "options": "boyau naturel",
        "poidsEstimeG": 1000,
        "poidsReelG": 980,
        "ecartPct": -2,
        "prixKg": 18.75,
        "prixFinal": 18.38,
        "pese": true
      },
      {
        "nom": "Chipolata maison",
        "quantite": 1,
        "prix": 22.49,
        "options": "au thym",
        "poidsEstimeG": 1500,
        "poidsReelG": 1500,
        "ecartPct": 0,
        "prixKg": 14.99,
        "prixFinal": 22.49,
        "pese": true
      },
      {
        "nom": "Boudin noir maison",
        "quantite": 1,
        "prix": 11.55,
        "options": "—",
        "poidsEstimeG": 700,
        "poidsReelG": 700,
        "ecartPct": 0,
        "prixKg": 16.5,
        "prixFinal": 11.55,
        "pese": true
      }
    ],
    "paye": 11.0,
    "totalFinal": 52.42,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "08/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "08/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-0991",
    "date": "04/10/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "NON_RETIREE",
    "statutLisible": "NON_RETIREE",
    "creneau": "dim. 4 oct. · 18h30–19h00",
    "boutiqueId": "store_sannois",
    "clientNom": "Élodie D.",
    "clientTelephone": "07 88 22 44 66",
    "total": 58.88,
    "arrhes": 12.0,
    "solde": 0,
    "lignes": [
      {
        "nom": "Côte de bœuf maturée 28 jours",
        "quantite": 1,
        "prix": 58.88,
        "options": "maturée 28 jours",
        "poidsEstimeG": 1200,
        "poidsReelG": 1180,
        "ecartPct": -1.7,
        "prixKg": 49.9,
        "prixFinal": 58.88,
        "pese": true
      }
    ],
    "paye": 12.0,
    "totalFinal": 58.88,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "04/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "04/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-0995",
    "date": "24/09/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "CLOTUREE",
    "statutLisible": "CLOTUREE",
    "creneau": "jeu. 24 sept. · 10h00–10h30",
    "boutiqueId": "store_sannois",
    "clientNom": "Jean-Pierre M.",
    "clientTelephone": "06 45 78 12 30",
    "total": 27.86,
    "arrhes": 0,
    "solde": 0,
    "lignes": [
      {
        "nom": "Bœuf bourguignon (morceaux à mijoter)",
        "quantite": 1,
        "prix": 28.86,
        "options": "2 kg en morceaux",
        "poidsEstimeG": 1400,
        "poidsReelG": 1450,
        "ecartPct": 3.6,
        "prixKg": 19.9,
        "prixFinal": 28.86,
        "pese": true
      }
    ],
    "paye": 28.86,
    "totalFinal": 28.86,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "24/09/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "24/09/2026"
      },
      {
        "titre": "En préparation",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1006",
    "date": "27/09/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "CLOTUREE",
    "statutLisible": "CLOTUREE",
    "creneau": "dim. 27 sept. · 11h00–11h30",
    "boutiqueId": "store_franconville",
    "clientNom": "Thomas R.",
    "clientTelephone": "06 33 77 88 99",
    "total": 43.04,
    "arrhes": 0,
    "solde": 0,
    "lignes": [
      {
        "nom": "Gigot d'agneau raccourci",
        "quantite": 1,
        "prix": 45.19,
        "options": "raccourci, prêt à rôtir",
        "poidsEstimeG": 1600,
        "poidsReelG": 1700,
        "ecartPct": 6.25,
        "prixKg": 26.9,
        "prixFinal": 45.19,
        "pese": true
      }
    ],
    "paye": 45.19,
    "totalFinal": 45.19,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "27/09/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "27/09/2026"
      },
      {
        "titre": "En préparation",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1014",
    "date": "01/10/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "CLOTUREE",
    "statutLisible": "CLOTUREE",
    "creneau": "jeu. 1 oct. · 09h30–10h00",
    "boutiqueId": "store_sannois",
    "clientNom": "Jean-Pierre M.",
    "clientTelephone": "06 45 78 12 30",
    "total": 42.21,
    "arrhes": 0,
    "solde": 0,
    "lignes": [
      {
        "nom": "Entrecôte maturée 28 jours",
        "quantite": 1,
        "prix": 44.32,
        "options": "2 cm d’épaisseur",
        "poidsEstimeG": 900,
        "poidsReelG": 970,
        "ecartPct": 7.8,
        "prixKg": 46.9,
        "prixFinal": 44.32,
        "pese": true
      }
    ],
    "paye": 44.32,
    "totalFinal": 44.32,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "01/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "01/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1024",
    "date": "10/10/2026",
    "mode": "PICKUP",
    "modeLisible": "Retrait en boutique",
    "statut": "CLOTUREE",
    "statutLisible": "CLOTUREE",
    "creneau": "sam. 10 oct. · 18h30–19h00",
    "boutiqueId": "store_sannois",
    "clientNom": "Élodie D.",
    "clientTelephone": "07 88 22 44 66",
    "total": 315.0,
    "arrhes": 100.0,
    "solde": 0,
    "lignes": [
      {
        "nom": "Côte de bœuf Wagyu croisé",
        "quantite": 1,
        "prix": 330.75,
        "options": "maturée 40 jours, 2 pièces",
        "poidsEstimeG": 2100,
        "poidsReelG": 2310,
        "ecartPct": 10,
        "prixKg": 150.0,
        "prixFinal": 330.75,
        "pese": true
      }
    ],
    "paye": 330.75,
    "totalFinal": 330.75,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "10/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "10/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  },
  {
    "numero": "CMD-2026-1059",
    "date": "20/10/2026",
    "mode": "DELIVERY",
    "modeLisible": "Livraison réfrigérée",
    "statut": "EN_PREPARATION",
    "statutLisible": "En préparation",
    "creneau": "mar. 20 oct. · 9h–13h (Chronofresh)",
    "boutiqueId": "store_sannois",
    "clientNom": "Thomas R.",
    "clientTelephone": "06 33 77 88 99",
    "total": 34.04,
    "arrhes": 34.04,
    "solde": 0,
    "lignes": [
      {
        "nom": "Chipolata maison",
        "quantite": 1,
        "prix": 22.48,
        "options": "au thym",
        "poidsEstimeG": 1500,
        "poidsReelG": null,
        "ecartPct": null,
        "prixKg": 14.99,
        "prixFinal": null,
        "pese": false
      },
      {
        "nom": "Boudin noir maison",
        "quantite": 1,
        "prix": 11.55,
        "options": "—",
        "poidsEstimeG": 700,
        "poidsReelG": null,
        "ecartPct": null,
        "prixKg": 16.5,
        "prixFinal": null,
        "pese": false
      }
    ],
    "paye": 34.04,
    "totalFinal": null,
    "suivi": [
      {
        "titre": "Commande reçue",
        "fait": true,
        "date": "20/10/2026"
      },
      {
        "titre": "Découpe planifiée",
        "fait": true,
        "date": "20/10/2026"
      },
      {
        "titre": "En préparation",
        "fait": true,
        "date": "20/10/2026"
      },
      {
        "titre": "Prête au retrait",
        "fait": false,
        "date": ""
      },
      {
        "titre": "Terminée",
        "fait": false,
        "date": ""
      }
    ]
  }
];

export const COMPTE: Compte = {
  "prenom": "Nadia",
  "nom": "B.",
  "email": "nadia.b@example.fr",
  "telephone": "06 12 34 56 78",
  "depuis": "2026",
  "tier": "NOUVEAU",
  "fidelite": {
    "palier": "Graine de cumin",
    "points": 124,
    "prochain": 250
  },
  "adresses": [
    {
      "id": "adr_1",
      "libelle": "Domicile",
      "defaut": true,
      "ligne": "12 rue des Lilas",
      "complement": "Bâtiment B, 2e étage",
      "codePostal": "95210",
      "ville": "Saint-Gratien",
      "cp": "95210"
    },
    {
      "id": "adr_2",
      "libelle": "Bureau",
      "defaut": false,
      "ligne": "4 avenue de la Gare",
      "complement": "",
      "codePostal": "95120",
      "ville": "Sannois",
      "cp": "95120"
    }
  ],
  "boutiqueFavorite": "store_sannois",
  "preferences": {
    "mode": "PICKUP",
    "courriel": true,
    "sms": true,
    "coupures": "Épaisseur 3 cm, ficelée"
  }
};
